import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';
import { toIsoDate } from '../utils/eventDates';
import { EVENTS_ENABLED } from '../constants/featureFlags';

// ── In-memory cache (no localStorage for app data) ──
let eventsCache: StoredEvent[] = [];
let loadedOnce = false;

/** Fired after the cache is changed by a create, update, archive or restore. Settings persists events on this one. */
export const EVENTS_CHANGED_EVENT = 'anojeje:events-changed';
/** Fired after a refresh from the API. Pickers re-read the cache on it; nothing is persisted from it. */
export const EVENTS_LOADED_EVENT = 'anojeje:events-loaded';

export interface RsvpCounts {
  invited: number;
  confirmed: number;
  declined: number;
  pending: number;
}

export interface StoredEvent {
  /** uuid, kept as text. */
  id: string;
  title: string;
  category: string;
  type: string;
  status: string;
  image: string;
  date: string;       // stored text: ISO ("2026-12-12") or an older display string
  time: string;       // display string e.g. "8:00 AM"
  startTime?: string; // 24hr e.g. "08:00"
  endTime?: string;   // 24hr e.g. "16:00"
  endDate?: string;   // "YYYY-MM-DD" — undefined/same as date means single-day
  timezone?: string;
  location: string;
  venue?: string;
  venueId?: string;
  address?: string;
  city?: string;
  googleMapsLink?: string;
  progress: number;
  /** Exact amount as text ('' when none or hidden from this role). The currency is the project's. */
  price: string;
  description: string;
  notes?: string;
  dresscode?: string;
  /** Planned headcount. */
  headcount?: number;
  /** Guests who answered "confirmed" (read-only, from the RSVPs). */
  headcountConfirmed?: number;
  rsvpCounts?: RsvpCounts;
  guestGroups?: unknown[];
  eventColor?: string;
  highlights?: unknown[];
  vendors?: unknown[];
  budgetAllocation?: string;
  theme?: string;
  capacity?: number;
  order: number;
  isCustom?: boolean;
  archived?: boolean;
  archivedAt?: string | null;
}

export function formatDisplayTime(time24: string): string {
  if (!time24) return 'TBD';
  const [h, m] = time24.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 'TBD';
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

// ── mapping between the API (snake_case) and the screens (camelCase) ──

type ApiEvent = Record<string, unknown>;

const text = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
// pg returns exact money as text (or a number from an older build); keep it as text.
const amount = (v: unknown): string | undefined => (v === null || v === undefined || v === '' ? undefined : String(v));

export function fromApiEvent(e: ApiEvent): StoredEvent {
  const startTime = text(e.start_time);
  return {
    id: String(e.id),
    title: String(e.title ?? ''),
    category: text(e.category) ?? '',
    type: text(e.type) ?? '',
    status: text(e.status) ?? 'upcoming',
    image: text(e.image) ?? '',
    date: text(e.date) ?? '',
    time: text(e.time) ?? (startTime ? formatDisplayTime(startTime) : ''),
    startTime,
    endTime: text(e.end_time),
    endDate: text(e.end_date),
    timezone: text(e.timezone),
    location: text(e.location) ?? '',
    venue: text(e.venue),
    venueId: text(e.venue_id),
    address: text(e.address),
    city: text(e.city),
    googleMapsLink: text(e.google_maps_link),
    progress: num(e.progress) ?? 0,
    price: amount(e.price) ?? '',
    description: text(e.description) ?? '',
    notes: text(e.notes),
    dresscode: text(e.dresscode),
    headcount: num(e.headcount),
    headcountConfirmed: num(e.headcount_confirmed),
    rsvpCounts: e.rsvp_counts && typeof e.rsvp_counts === 'object' ? (e.rsvp_counts as RsvpCounts) : undefined,
    guestGroups: Array.isArray(e.guest_groups) ? e.guest_groups : undefined,
    eventColor: text(e.event_color),
    highlights: Array.isArray(e.highlights) ? e.highlights : undefined,
    vendors: Array.isArray(e.vendors) ? e.vendors : undefined,
    budgetAllocation: amount(e.budget_allocation),
    theme: text(e.theme),
    capacity: num(e.capacity),
    order: num(e.sort_order) ?? 0,
    isCustom: e.is_custom === true,
    archived: e.archived === true || !!e.archived_at,
    archivedAt: text(e.archived_at) ?? null,
  };
}

const NULLABLE_TEXT: Array<[keyof StoredEvent, string]> = [
  ['category', 'category'], ['type', 'type'], ['status', 'status'], ['image', 'image'], ['time', 'time'],
  ['startTime', 'start_time'], ['endTime', 'end_time'], ['endDate', 'end_date'], ['timezone', 'timezone'],
  ['location', 'location'], ['venue', 'venue'], ['venueId', 'venue_id'], ['address', 'address'], ['city', 'city'],
  ['googleMapsLink', 'google_maps_link'], ['description', 'description'], ['notes', 'notes'],
  ['dresscode', 'dresscode'], ['eventColor', 'event_color'], ['theme', 'theme'],
];

/** Body for POST/PUT. Only keys present on `data` are sent; an empty text clears the field. Money goes as a number. */
export function toApiPayload(data: Partial<StoredEvent>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (data.title !== undefined) out.title = data.title;
  if (data.date !== undefined) out.date = data.date === '' ? null : toIsoDate(data.date) || data.date;
  for (const [key, apiKey] of NULLABLE_TEXT) {
    const v = data[key];
    if (v !== undefined) out[apiKey] = v === '' ? null : v;
  }
  if (data.progress !== undefined) out.progress = data.progress;
  if (data.headcount !== undefined) out.headcount = data.headcount;
  if (data.capacity !== undefined) out.capacity = data.capacity;
  if (data.price !== undefined) out.price = data.price === '' ? null : Number(data.price);
  if (data.budgetAllocation !== undefined) out.budget_allocation = data.budgetAllocation === '' ? null : Number(data.budgetAllocation);
  if (data.guestGroups !== undefined) out.guest_groups = data.guestGroups;
  if (data.highlights !== undefined) out.highlights = data.highlights;
  if (data.vendors !== undefined) out.vendors = data.vendors;
  if (data.order !== undefined) out.sort_order = data.order;
  if (data.isCustom !== undefined) out.is_custom = data.isCustom;
  return out;
}

// ── cache ──

/** Sync load from in-memory cache (active events only). */
export function loadEvents(): StoredEvent[] {
  return eventsCache;
}

/** True once a refresh from the API has finished (successfully or not). */
export function eventsLoaded(): boolean {
  return loadedOnce;
}

function notifyEventsChanged(events: StoredEvent[]) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<StoredEvent[]>(EVENTS_CHANGED_EVENT, { detail: events }));
}

function notifyEventsLoaded(events: StoredEvent[]) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<StoredEvent[]>(EVENTS_LOADED_EVENT, { detail: events }));
}

export function saveEvents(events: StoredEvent[], options: { silent?: boolean } = {}): void {
  eventsCache = events;
  if (!options.silent) notifyEventsChanged(events);
}

/** Forget everything cached (the project changed or the person signed out). */
export function resetEventsCache(): void {
  eventsCache = [];
  loadedOnce = false;
}

const sortEvents = (list: StoredEvent[]) => [...list].sort((a, b) => a.order - b.order || a.date.localeCompare(b.date));

// ── API ──

function unwrapList(raw: unknown): ApiEvent[] {
  if (Array.isArray(raw)) return raw as ApiEvent[];
  const events = (raw as { events?: unknown })?.events;
  return Array.isArray(events) ? (events as ApiEvent[]) : [];
}

/**
 * Pull the project's active events into the cache. An empty answer is real
 * (a project with no events must show none), so it replaces the cache; a failed
 * request keeps what is cached and, with `strict`, rethrows so a screen can say so.
 */
export async function refreshEventsFromApi(options: { strict?: boolean } = {}): Promise<StoredEvent[]> {
  // Module switched off: no request at all, whoever asks (pickers, calendar, the sync).
  if (!EVENTS_ENABLED) return eventsCache;
  try {
    const raw = await apiGet<unknown>(planningPath('/events'));
    const list = sortEvents(unwrapList(raw).map(fromApiEvent));
    eventsCache = list;
    loadedOnce = true;
    notifyEventsLoaded(list);
    return list;
  } catch (err) {
    // loadedOnce stays as it was, so a picker that mounts later tries again.
    if (options.strict) throw err;
    return eventsCache;
  }
}

/** Archived events, fetched on demand (never cached with the active ones). */
export async function fetchArchivedEvents(): Promise<StoredEvent[]> {
  const raw = await apiGet<unknown>(planningPath('/events?archived=true'));
  return sortEvents(unwrapList(raw).map(fromApiEvent));
}

/** One event by id, archived or not, with its RSVP counts. */
export async function fetchEvent(id: string): Promise<StoredEvent> {
  return fromApiEvent(await apiGet<ApiEvent>(planningPath(`/events/${encodeURIComponent(id)}`)));
}

function upsertInCache(event: StoredEvent): void {
  const without = eventsCache.filter(e => e.id !== event.id);
  eventsCache = event.archived ? without : sortEvents([...without, event]);
}

/** Create an event; resolves with the saved row (the API assigns the uuid). Throws on failure. */
export async function createEvent(data: Partial<StoredEvent> & { title: string }): Promise<StoredEvent> {
  const created = fromApiEvent(await apiPost<ApiEvent>(planningPath('/events'), toApiPayload({ isCustom: true, ...data })));
  upsertInCache(created);
  notifyEventsChanged(eventsCache);
  return created;
}

export async function updateEvent(id: string, data: Partial<StoredEvent>): Promise<StoredEvent> {
  const updated = fromApiEvent(await apiPut<ApiEvent>(planningPath(`/events/${encodeURIComponent(id)}`), toApiPayload(data)));
  upsertInCache(updated);
  notifyEventsChanged(eventsCache);
  return updated;
}

export async function archiveEvent(id: string): Promise<StoredEvent> {
  const archived = fromApiEvent(await apiPost<ApiEvent>(planningPath(`/events/${encodeURIComponent(id)}/archive`), {}));
  eventsCache = eventsCache.filter(e => e.id !== id);
  notifyEventsChanged(eventsCache);
  return archived;
}

export async function restoreEvent(id: string): Promise<StoredEvent> {
  const restored = fromApiEvent(await apiPost<ApiEvent>(planningPath(`/events/${encodeURIComponent(id)}/restore`), {}));
  upsertInCache(restored);
  notifyEventsChanged(eventsCache);
  return restored;
}

/** Permanent delete. The API refuses unless the event is archived and nothing still uses it. */
export async function deleteEventPermanently(id: string): Promise<void> {
  await apiDelete(planningPath(`/events/${encodeURIComponent(id)}`));
  eventsCache = eventsCache.filter(e => e.id !== id);
}
